// #region imports

// --- Angular
import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";

// --- RxJs
import { Observable, catchError, map, of } from "rxjs";

// --- 3rd Party
import { JL } from 'jsnlog';
import { retryBackoff } from "backoff-rxjs";
import { TranslateService } from "@ngx-translate/core";

// --- HS Code
import { RemoteCommand, CommandResult } from "../../../models";
import { CommandHandler, CommandHandlerResult } from "../system/command-handler";
import { environment } from "../../../../environments/environment";

// #endregion


@Injectable({
  providedIn: 'root'
})
export class ParticipantGroupCommandHandler extends CommandHandler {
  private _url: string = `${environment.apiConfig.surveyApiUrl}${environment.apiConfig.participantsGroupsApi}/executecommands`

  constructor(
    @Inject('JSNLOG') private _logger: JL.JSNLog,
    private _httpClient: HttpClient,
    private _translate: TranslateService
  ) {
    super();
  }

  protected override executeCommands(commands: RemoteCommand[]): Observable<CommandHandlerResult> {

    const dtos = commands.map(c => c.createCommandDto());

    return this._httpClient.post<CommandResult[]>(this._url, dtos).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._error.bind(this)),
      map((results: CommandResult[] | CommandHandlerResult) => {
        if (Array.isArray(results)) {
          results.forEach(r => {
            const command: RemoteCommand | undefined = this._commands.find(c => c.commandId === r.commandId);
            if (command) {
              command.commandResult = r;
              command.isExecuted = true;
            }
          });
          const isSuccess = results.every(r => r.isSuccessful);
          const errorMessage = isSuccess ? '' : this._translate.instant('v2GeneralCommandErrorMessage');
          return {
            isSuccess,
            errorMessage,
            results
          } as CommandHandlerResult;
        }
        return results;
      })
    );
  }

  private _error(err: HttpErrorResponse): Observable<CommandHandlerResult> {
    const correlationId = err.headers.get('correlationId');
    this._logger().error({ errorMessage: 'An error occurred attempting to run a series of participant group commands.', error: err, correlationId: correlationId});
    const errorMessage: string = this._translate.instant('v2GeneralCommandErrorMessage');
    return of({ isSuccess: false, errorMessage } as CommandHandlerResult)
  }
}
